import Campaign from '../store/Campaign';
import React, { HTMLAttributes } from 'react';
import ReactDOM from 'react-dom';
import { ButtonType } from 'mini-button/lib/components/Button.types';
import { CAMPAIGN_CLICKED, CAMPAIGN_DISMISSED } from '../utils/eventNames';
import { CampaignEndSignal } from '../store/schema/CampaignEndSignal';
import { CampaignQueue } from '../store/CampaignQueue';
import { CampaignType } from '../store/schema/CampaignType';
import { CustomData } from 'mini-app-context';
import { endCampaign } from '../utils/endCampaign';
import { getCoverImageUrl, getIconImageSource } from './utils/getImageSourceUtil';
import { getDataLogger, LogOptions } from 'telemetry';
import { getModifiedViewProps } from './utils/getModifiedViewProps';
import { getOpxHostAppName } from 'mini-opx-config/lib/getOpxHostAppName';
import { IconInfo } from 'mini-embedded-messagebox';
import { LazyBanner } from 'mini-banner';
import { mergeOverrideStyles } from 'mini-styles/lib/utils/mergeOverrideStyles';
import { mergeStyles } from '@uifabric/merge-styles';

const styles = require('./CampaignBanner.scss');

interface CustomLogParamsType {
    logData?: CustomData;
    logOptions?: LogOptions;
}

export interface CampaignBannerConfig {
    title: string;
    subtitle: string;
    icon: IconInfo | Function;
    closeButtonAriaLabel: string;
    primaryButtonText: string;
    imageSrc?: string;
    refId?: string;
    coverImageURL?: string;
    darkModeCoverImageURL?: string;
    onCampaignDismiss?: () => void;
    onCampaignMessageClick?: () => void;
    onPrimaryButtonClick?: () => void;
    onSecondaryButtonClick?: () => void;
    onMount?: () => void;
    customLogParams?: CustomLogParamsType;
    withoutBubble?: boolean;
    messageWrapperElementProps?: HTMLAttributes<HTMLDivElement>;
    rootElementProps?: HTMLAttributes<HTMLDivElement>;
    onRender?: () => void;
    isPartOfCarousel?: string;
    showCloseButton?: string;
    coverImageHeight?: string;
    customStyles?: {};
}

/**
 * Class to instantiate a banner campaign type object.
 */
export class CampaignBanner implements CampaignType<CampaignBannerConfig> {
    logger = getDataLogger();
    private static bannerQueue = CampaignQueue.getInstance();

    public start(campaign: Campaign<CampaignBannerConfig>) {
        CampaignBanner.bannerQueue.addToQueue(campaign);
    }

    public end(campaign: Campaign<CampaignBannerConfig>, endSignal: CampaignEndSignal) {
        endCampaign(campaign, endSignal);
    }

    private logCampaignUsage(
        eventName: string,
        campaign: Campaign<CampaignBannerConfig>,
        customLogParams?: CustomLogParamsType
    ) {
        this.logger?.(
            eventName,
            {
                id: campaign.getRemoteCampaignId(),
                isUIAction: true,
                hostApp: getOpxHostAppName(),
                ...customLogParams?.logData,
            },
            { isCore: true, ...customLogParams?.logOptions }
        );
    }

    private logAndEndCampaign(
        eventName: string,
        campaign: Campaign<CampaignBannerConfig>,
        endSignal: CampaignEndSignal,
        customLogParams?: CustomLogParamsType
    ) {
        // Log usage DP
        this.logCampaignUsage(eventName, campaign, customLogParams);

        // End the campaign
        this.end(campaign, endSignal);
    }

    /**
     * This is called from the render function of the Campaign object
     * @param campaign Campaign object instance
     * @param viewProps Props to be passed to the view component
     */
    public render(
        campaign: Campaign<CampaignBannerConfig>,
        viewProps: CampaignBannerConfig
    ): JSX.Element {
        const self = this;
        const {
            onCampaignDismiss,
            onCampaignMessageClick,
            customLogParams,
            onRender,
            onPrimaryButtonClick,
            onSecondaryButtonClick,
            coverImageURL,
            showCloseButton = 'true',
            coverImageHeight,
            customStyles = {},
        } = viewProps;
        onRender?.();

        const onCloseButtonClick = () => {
            onCampaignDismiss?.();
            self.logAndEndCampaign(
                CAMPAIGN_DISMISSED,
                campaign,
                CampaignEndSignal.OnUserDismiss,
                customLogParams
            );
        };

        const onPrimaryButtonClickHandler = () => {
            onPrimaryButtonClick?.();
            onCampaignMessageClick?.();
            self.logAndEndCampaign(
                CAMPAIGN_CLICKED,
                campaign,
                CampaignEndSignal.OnClick,
                customLogParams
            );
        };

        const onSecondaryButtonClickHandler = () => {
            onSecondaryButtonClick?.();
            self.logAndEndCampaign(
                CAMPAIGN_DISMISSED,
                campaign,
                CampaignEndSignal.OnUserDismiss,
                customLogParams
            );
        };

        const getIconInfo = (): IconInfo => {
            const imageSource = getIconImageSource(viewProps);

            if (imageSource) {
                return {
                    iconDataType: 'Svg',
                    iconData: imageSource,
                };
            } else {
                return null;
            }
        };

        const getBannerCustomStyles = () => {
            const result = {};
            for (const key of Object.keys(customStyles)) {
                result[key] = styles[viewProps.customStyles[key]];
            }

            return result;
        };

        const getBannerComponent = () => {
            const iconInfo: IconInfo = getIconInfo();
            return (
                <LazyBanner
                    {...viewProps}
                    {...getModifiedViewProps(viewProps)}
                    {...(iconInfo && { icon: iconInfo })}
                    {...(coverImageURL && { coverImageURL: getCoverImageUrl(viewProps) })}
                    onCloseButtonClick={onCloseButtonClick}
                    onPrimaryButtonClick={onPrimaryButtonClickHandler}
                    onSecondaryButtonClick={onSecondaryButtonClickHandler}
                    primaryButtonType={ButtonType.Link}
                    secondaryButtonType={ButtonType.Link}
                    bannerStyles={{
                        icon: styles.icon,
                        title: styles.title,
                        subtitle: styles.subtitle,
                        closeButtonContainer: styles.closeButton,
                        textContainer: styles.textContainer,
                        primaryButtonContainer: styles.primaryButtonContainer,
                        secondaryButtonContainer: styles.secondaryButtonContainer,
                        primaryButtonClassNameOverride: styles.primaryButtonClassNameOverride,
                        secondaryButtonClassNameOverride: styles.secondaryButtonClassNameOverride,
                        buttonContainer: styles.buttonContainer,
                        buttonSection: styles.buttonSection,
                        coverImage: mergeStyles(
                            styles.coverImage,
                            coverImageHeight &&
                                mergeOverrideStyles({ height: `${coverImageHeight}px` })
                        ),
                        ...getBannerCustomStyles(),
                    }}
                    areButtonsReversed={true}
                    showCloseButton={JSON.parse(showCloseButton)}
                />
            );
        };

        if (viewProps.refId) {
            const documentToAttachTo = document.getElementById(viewProps.refId);
            documentToAttachTo && ReactDOM.render(getBannerComponent(), documentToAttachTo);
        } else {
            return getBannerComponent();
        }
        return null;
    }
}
